import { z } from '@hono/zod-openapi';

export const ORGANIZATION_TYPES = [
  'National Charity | Non-Profit',
  'Local Charity | Non-Profit',
  'Faith Community',
  'Business or Brand',
  'Network of Organizations',
  'School or University',
  'Government Agency'
] as const;

export const PARTICIPATION_INTERESTS = [
  'Host a Serve Event(s) at a location of your choice',
  'Mobilize Volunteers to a nearby Serve Event Location',
  'Provide goods and/or services through other Serve Events Regionally or Nationally'
] as const;

export const LANGUAGE_PREFERENCES = [
  'English',
  'Spanish',
  'Bilingual',
  'Other (please specify)'
] as const;

export const REFERRAL_SOURCES = [
  'Our website',
  'Newsletter',
  'Referral',
  'Social Media',
  'Google',
  'Webinar',
  'Blog | Article',
  'Other (please specify)'
] as const;

export const slugSchema = z
  .string()
  .min(3, { message: 'URL slug must be at least 3 characters' })
  .max(60, { message: 'URL slug must be at most 60 characters' })
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message:
      'URL slug must be lowercase letters, numbers, and hyphens only (no leading/trailing hyphens)'
  })
  .openapi({ description: 'URL-friendly organization slug' });

export const registerOrganizationRequestSchema = z
  .object({
    organizationName: z
      .string()
      .min(2, { message: 'Organization name is required' })
      .openapi({ description: 'Organization name' }),
    slug: slugSchema,
    websiteUrl: z
      .string()
      .url({ message: 'Please enter a valid URL' })
      .optional()
      .or(z.literal(''))
      .openapi({ description: 'Organization website URL' }),
    einNumber: z
      .string()
      .regex(/^\d+$/, { message: 'EIN must contain only numbers' })
      .optional()
      .or(z.literal(''))
      .openapi({
        description: 'Organization EIN (Employer Identification Number)'
      }),
    organizationType: z
      .string()
      .min(1, { message: 'Please select an organization type' })
      .openapi({ description: 'Type of organization' }),
    streetAddress: z
      .string()
      .min(1, { message: 'Street address is required' })
      .openapi({ description: 'Organization street address' }),

    firstName: z
      .string()
      .min(1, { message: 'First name is required' })
      .openapi({ description: 'Primary contact first name' }),
    lastName: z
      .string()
      .min(1, { message: 'Last name is required' })
      .openapi({ description: 'Primary contact last name' }),
    email: z
      .string()
      .email({ message: 'Please enter a valid email address' })
      .openapi({ description: 'Primary contact email' }),
    phone: z
      .string()
      .min(1, { message: 'Phone number is required' })
      .openapi({ description: 'Primary contact phone number' }),

    hasMultipleLocations: z.string().optional().openapi({
      description:
        'Whether the organization has multiple locations that will host events'
    }),
    numberOfLocations: z.number().optional().openapi({
      description:
        'Number of locations that will host events (when hasMultipleLocations is Yes)'
    }),
    networkDenominationAssociation: z.string().optional().openapi({
      description:
        'Whether associated with a participating network or denomination'
    }),
    networkDenomination: z
      .string()
      .optional()
      .openapi({ description: 'Network or denomination that referred you' }),
    participationInterests: z.array(z.string()).optional().openapi({
      description:
        'How the organization hopes to participate (multiple selections allowed)'
    }),
    participationInterestOther: z.string().optional().openapi({
      description: 'Custom participation interest when Other is selected'
    }),
    participationInterestGoodsServices: z.string().optional().openapi({
      description:
        'Description of goods/services and quantity when Provide Goods/Services is selected'
    }),
    languagePreference: z
      .string()
      .optional()
      .openapi({ description: 'Preferred language for communication' }),
    languagePreferenceOther: z.string().optional().openapi({
      description: 'Custom language preference when Other is selected'
    }),
    referralSource: z
      .string()
      .optional()
      .openapi({ description: 'How did you hear about us' }),
    referralSourceOther: z
      .string()
      .optional()
      .openapi({ description: 'Custom referral source when Other is selected' })
  })
  .openapi({
    description: 'Request body for Partner Interest Form'
  });

export const sendRegistrationEmailOtpRequestSchema =
  registerOrganizationRequestSchema.extend({}).openapi({
    description:
      'Payload for starting partner registration and sending an email verification code'
  });

export const sendRegistrationEmailOtpResponseSchema = z
  .object({
    data: z.object({
      status: z.literal('sent').openapi({ description: 'OTP email dispatched' })
    })
  })
  .openapi({ description: 'Response when registration email OTP is sent' });

export const organizationRegistrationResponseSchema = z
  .object({
    organization: z
      .object({
        id: z.string().uuid().openapi({ description: 'Organization ID' }),
        name: z
          .string()
          .openapi({ description: 'Registered organization name' }),
        slug: z
          .string()
          .openapi({ description: 'URL-friendly organization slug' })
      })
      .openapi({ description: 'Created organization record' }),
    user: z
      .object({
        id: z.string().uuid().openapi({ description: 'User ID' }),
        firstName: z
          .string()
          .openapi({ description: 'Primary contact first name' }),
        lastName: z
          .string()
          .openapi({ description: 'Primary contact last name' }),
        email: z.string().email().openapi({ description: 'Contact email' }),
        phone: z.string().openapi({ description: 'Contact phone' }),
        roles: z
          .array(z.string())
          .openapi({ description: 'Roles assigned to the user' })
      })
      .openapi({ description: 'Created user record' })
  })
  .openapi({
    description: 'Response body for organization registration'
  });

export const verifyRegistrationEmailOtpRequestSchema = z
  .object({
    email: z.string().email().openapi({ description: 'Email being verified' }),
    code: z
      .string()
      .regex(/^[0-9]{6}$/)
      .length(6)
      .openapi({ description: 'Six digit verification code' })
  })
  .openapi({
    description: 'Request body for verifying registration email OTP'
  });

export const verifyRegistrationEmailOtpResponseSchema = z
  .object({
    data: organizationRegistrationResponseSchema
  })
  .openapi({
    description:
      'Response when registration email OTP is verified and registration is created'
  });

export type RegisterOrganizationRequest = z.infer<
  typeof registerOrganizationRequestSchema
>;
export type RegisterOrganizationResponse = z.infer<
  typeof organizationRegistrationResponseSchema
>;
export type SendRegistrationEmailOtpRequest = z.infer<
  typeof sendRegistrationEmailOtpRequestSchema
>;
export type SendRegistrationEmailOtpResponse = z.infer<
  typeof sendRegistrationEmailOtpResponseSchema
>;
export type VerifyRegistrationEmailOtpRequest = z.infer<
  typeof verifyRegistrationEmailOtpRequestSchema
>;
export type VerifyRegistrationEmailOtpResponse = z.infer<
  typeof verifyRegistrationEmailOtpResponseSchema
>;
